import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/api';
import { resolveMediaUrl } from '@/utils/helpers';
import { GameCard } from '@/components/common/GameCard';
import { VideoTheater } from '@/components/common/VideoTheater';

const REGION_LABEL = { west: 'West', east: 'East', central: 'Central', south: 'South' };

export const LeaguePage = ({ leagues }) => {
  const { slug } = useParams();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);

  const conference = leagues?.find(l => l.slug === slug);

  useEffect(() => {
    const loadData = async () => {
      if (!conference) return;
      try {
        const gamesRes = await api.getGames({ league_id: conference.id });
        setGames(gamesRes.data);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [conference]);

  if (!conference) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-400" data-testid="conference-not-found">Conference not found</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="conference-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        {conference.logo && (
          <img src={resolveMediaUrl(conference.logo)} alt={conference.name} className="h-16 w-16 object-contain" />
        )}
        <div>
          <div className="text-xs uppercase tracking-widest text-wpsl-red font-bold mb-1">
            {conference.region ? `${REGION_LABEL[conference.region] || conference.region} Region` : 'Conference'}
          </div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {conference.name}
          </h1>
          <p className="text-gray-400">Upcoming matches and replays</p>
        </div>
      </div>

      <h2 className="text-xl font-semibold text-white mb-4">Matches</h2>
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-gray-800/50 rounded-lg p-4 animate-pulse">
              <div className="h-20 bg-gray-700 rounded" />
            </div>
          ))}
        </div>
      ) : games.length === 0 ? (
        <div className="bg-gray-800/50 rounded-lg p-8 text-center border border-gray-700">
          <p className="text-gray-400" data-testid="conference-no-games">No matches scheduled for this conference.</p>
        </div>
      ) : (
        <div className="grid gap-4" data-testid="conference-games-list">
          {games.map(game => (
            <GameCard key={game.id} game={game} onWatch={setSelectedGame} />
          ))}
        </div>
      )}

      <VideoTheater
        game={selectedGame}
        isOpen={!!selectedGame}
        onClose={() => setSelectedGame(null)}
      />
    </div>
  );
};

export default LeaguePage;
